import { useState, useCallback } from 'react';
import { uploadService } from '../api/upload.service';

interface UseFileUploadReturn {
  uploading: boolean;
  progress: number;
  error: string | null;
  upload: (
    file: File,
    module: string,
  ) => Promise<{ path: string; url: string } | null>;
  reset: () => void;
}

export const useFileUpload = (): UseFileUploadReturn => {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const reset = useCallback(() => {
    setUploading(false);
    setProgress(0);
    setError(null);
  }, []);

  const upload = useCallback(
    async (
      file: File,
      module: string,
    ): Promise<{ path: string; url: string } | null> => {
      setUploading(true);
      setProgress(0);
      setError(null);

      try {
        // 1. Get signed URL
        const response = await uploadService.getUploadUrl(
          file.name,
          module,
          file.type,
        );

        if (!response.success || !response.data) {
          throw new Error('Failed to get upload URL');
        }

        const { signedUrl, path } = response.data;

        // 2. Upload file to signed URL
        await uploadService.uploadFileToUrl(
          signedUrl,
          file,
          file.type,
          (percent) => setProgress(percent),
        );

        setUploading(false);
        return { path, url: signedUrl.split('?')[0] }; // Return path and public-like URL (without token)
      } catch (err: any) {
        console.error('File upload failed:', err);
        setError(err.message || 'Failed to upload file');
        setUploading(false);
        return null;
      }
    },
    [],
  );

  return {
    uploading,
    progress,
    error,
    upload,
    reset,
  };
};
